import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { WelderDetailedAnalysis } from '../../types/stats';

interface PositionDefectRadarProps {
  data: WelderDetailedAnalysis;
}

// Helper function to sort positions in clock order (12, 1, 2, ..., 11)
const sortClockOrder = (positions: string[]): string[] => {
  // Extract numeric value from position string
  const getPositionNumber = (pos: string): number => {
    const normalized = pos.toLowerCase().trim();
    const match = normalized.match(/(\d+)/);
    if (match) {
      const num = parseInt(match[1]);
      if (num >= 1 && num <= 12) {
        return num === 12 ? 12 : num;
      }
    }
    return 99; // Unknown positions go to end
  };

  return positions.sort((a, b) => {
    const aNum = getPositionNumber(a);
    const bNum = getPositionNumber(b);
    
    // Handle 12 specially - it should come first
    if (aNum === 12 && bNum !== 12) return -1;
    if (bNum === 12 && aNum !== 12) return 1;
    if (aNum === 12 && bNum === 12) return 0;
    
    // For others, sort numerically
    return aNum - bNum;
  });
};

export const PositionDefectRadar = ({ data }: PositionDefectRadarProps) => {
  const allSeriesData = useMemo(() => {
    const positionDefectMatrix = data.positionDefectMatrix;
    let positions = sortClockOrder([...data.allPositions]);
    const defectTypes = data.allDefectTypes;

    if (positions.length === 0 || defectTypes.length === 0) {
      return { seriesData: [], positions: [], maxValue: 0 };
    }

    // Build radar chart data for each defect type (positions are now the axes)
    // We need to reorder values to match the reordered indicator positions
    const reversedForClockwise = positions.length > 1 
      ? [positions[0], ...positions.slice(1).reverse()]
      : positions;
    
    const seriesData = defectTypes.map((defect) => {
      // Map values to match the reordered indicator positions
      const values = reversedForClockwise.map((position) => {
        const positionData = positionDefectMatrix[position] || {};
        // Try exact match first, then case-insensitive
        let value = positionData[defect] || 0;
        if (value === 0) {
          const matchingKey = Object.keys(positionData).find(
            key => key.toLowerCase().trim() === defect.toLowerCase().trim()
          );
          if (matchingKey) {
            value = positionData[matchingKey];
          }
        }
        return value;
      });
      const total = values.reduce((sum, val) => sum + val, 0);
      
      return {
        value: values,
        name: defect,
        total,
      };
    }).filter(item => item.total > 0);

    const maxValue = Math.max(...seriesData.map(s => Math.max(...s.value)), 1);

    return { seriesData, positions, maxValue };
  }, [data]);

  const option = useMemo(() => {
    const { seriesData, positions, maxValue } = allSeriesData;

    if (seriesData.length === 0) {
      return null;
    }

    // To get clockwise order starting from 12 o'clock:
    // ECharts places indicators counter-clockwise, so we need [12, 11, 10, ..., 2, 1]
    // This way: 12 at top, then going counter-clockwise gives us 12, 11, 10, ..., 2, 1
    // But we want 12, 1, 2, ..., 11 clockwise, so we reverse except for 12
    const reversedForClockwise = positions.length > 1 
      ? [positions[0], ...positions.slice(1).reverse()]
      : positions;

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const defect = params.name;
          let tooltip = `<strong>${defect}</strong><br/>`;
          // Use the reordered positions to match the indicator order
          reversedForClockwise.forEach((position: string, idx: number) => {
            const value = params.value[idx];
            if (value > 0) {
              tooltip += `${position}: ${value}<br/>`;
            }
          });
          tooltip += `Total: ${params.data.total}`;
          return tooltip;
        },
      },
      legend: {
        data: seriesData.map(s => s.name),
        bottom: 10,
        type: 'plain', // Show all items without scrolling
        selector: [
          { type: 'all', title: 'Show All' },
          { type: 'inverse', title: 'Inverse' },
        ],
        itemGap: 10,
        textStyle: {
          fontSize: 11,
        },
      },
      radar: {
        indicator: reversedForClockwise.map((position) => ({
          name: position,
          max: maxValue,
        })),
        center: ['50%', '50%'],
        radius: '70%',
        startAngle: 90, // Start from top (12 o'clock) - 90 degrees
        nameGap: 8, // Distance between label and axis
        axisName: {
          color: '#333',
          fontSize: 12,
          fontWeight: 'bold',
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(200, 200, 200, 0.3)'],
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
      },
      series: [
        {
          name: 'Defect Type by Position',
          type: 'radar',
          data: seriesData,
          emphasis: {
            areaStyle: {
              opacity: 0.5,
            },
          },
        },
      ],
    } as echarts.EChartsOption;
  }, [allSeriesData]);

  if (!option || allSeriesData.seriesData.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No position vs defect type data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-semibold text-slate-900">Position vs Defect Type</h4>
      </div>
      <ReactECharts 
        option={option} 
        style={{ height: 500 }} 
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

